/**
 * Executive Dashboard — the app's home landing page.
 *
 * Rebuilt for the RMC conversion. The old version was an EPC "Project
 * Command Center" (active/delayed/completed project counts, contract
 * value, NCRs, safety incidents) sourced from a backend endpoint keyed
 * entirely on the now-removed Project entity — it would have shown zeros
 * forever. This reuses the RMC module dashboards already built and
 * verified this session (Production, Dispatch, Lab, Fleet, Equipment,
 * Approvals) rather than a new backend aggregator — one parallel fetch
 * per module, same pattern as routers/dpr.py's daily rollup.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../lib/api';
import { inrCompact } from '../lib/format';
import {
  FlaskConical, Truck, ClipboardCheck, Wrench, Bell, TrendingUp,
  CheckCircle2, XCircle, Clock, ArrowUpRight, Boxes,
} from 'lucide-react';

const Tile = ({ label, value, sub, icon: Icon, accent, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-white border border-slate-200 rounded-sm p-5 ${onClick ? 'cursor-pointer hover:border-[#1F7A6E]/40 transition-colors' : ''}`}
  >
    <div className="flex items-start justify-between mb-3">
      <div className="kpi-overline">{label}</div>
      <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${accent || 'bg-slate-100 text-slate-600'}`}>
        {Icon && <Icon size={15} />}
      </div>
    </div>
    <div className="kpi-value">{value}</div>
    {sub && <div className="mt-2 text-xs text-slate-500">{sub}</div>}
  </div>
);

const Section = ({ title, action, children }) => (
  <div className="bg-white border border-slate-200 rounded-sm">
    <div className="p-5 border-b border-slate-200 flex items-center justify-between">
      <h3 className="font-display font-bold text-base">{title}</h3>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [production, setProduction] = useState(null);
  const [dispatch, setDispatch] = useState(null);
  const [lab, setLab] = useState(null);
  const [fleet, setFleet] = useState(null);
  const [equipment, setEquipment] = useState(null);
  const [approvalsCount, setApprovalsCount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiGet('/production/dashboard').catch(() => null),
      apiGet('/dispatch/summary').catch(() => null),
      apiGet('/lab/dashboard').catch(() => null),
      apiGet('/fleet/dashboard').catch(() => null),
      apiGet('/equipment/fleet-dashboard').catch(() => null),
      apiGet('/approvals/pending/count').catch(() => null),
    ]).then(([p, d, l, f, e, a]) => {
      setProduction(p); setDispatch(d); setLab(l); setFleet(f); setEquipment(e);
      setApprovalsCount(a?.total ?? null);
    }).finally(() => setLoading(false));
  }, []);

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  if (loading) {
    return <div className="p-8 text-sm text-slate-500 font-mono">Loading dashboard…</div>;
  }

  return (
    <div>
      <div className="mb-7">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Executive Dashboard</div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">Plant Overview</h1>
        <p className="text-sm text-slate-600 mt-1">{today} — today's production, dispatch, quality, and fleet at a glance.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <Tile
          label="Production Today" value={production ? `${production.today_qty_m3} m³` : '—'}
          sub={production?.low_stock_materials?.length ? `${production.low_stock_materials.length} material(s) low stock` : 'Stock healthy'}
          icon={Boxes} accent={production?.low_stock_materials?.length ? 'bg-amber-50 text-amber-700' : 'bg-teal-50 text-[#1F7A6E]'}
          onClick={() => navigate('/app/production')}
        />
        <Tile
          label="Dispatch Today" value={dispatch ? dispatch.today_challan_count : '—'}
          sub={dispatch ? `${dispatch.today_qty_m3} m³ · ${inrCompact(dispatch.today_revenue)}` : ''}
          icon={Truck} accent="bg-emerald-50 text-emerald-700"
          onClick={() => navigate('/app/dispatch')}
        />
        <Tile
          label="Lab Pass Rate" value={lab?.pass_rate_pct != null ? `${lab.pass_rate_pct}%` : '—'}
          sub={lab ? `${lab.cube_tests_due} test(s) due` : ''}
          icon={FlaskConical}
          accent={lab?.pass_rate_pct != null && lab.pass_rate_pct < 95 ? 'bg-red-50 text-red-700' : 'bg-teal-50 text-[#1F7A6E]'}
          onClick={() => navigate('/app/lab')}
        />
        <Tile
          label="Fleet Trips Today" value={fleet ? fleet.today_trip_count : '—'}
          sub={fleet ? `${fleet.today_active_vehicles} vehicle(s) active` : ''}
          icon={ClipboardCheck} accent="bg-slate-100 text-slate-700"
          onClick={() => navigate('/app/fleet')}
        />
        <Tile
          label="Maintenance Due" value={equipment ? equipment.maintenance_due_count : '—'}
          sub={equipment ? `${equipment.deployed} working · ${equipment.idle} idle` : ''}
          icon={Wrench} accent={equipment?.maintenance_due_count > 0 ? 'bg-amber-50 text-amber-700' : 'bg-teal-50 text-[#1F7A6E]'}
          onClick={() => navigate('/app/equipment')}
        />
        <Tile
          label="Pending Approvals" value={approvalsCount ?? '—'}
          sub="Awaiting your action"
          icon={Bell} accent={approvalsCount > 0 ? 'bg-amber-50 text-amber-700' : 'bg-teal-50 text-[#1F7A6E]'}
          onClick={() => navigate('/app/approvals')}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Production — by grade today" action={
          <a href="/app/production" className="text-xs font-display font-bold uppercase tracking-widest text-[#1F7A6E] hover:underline flex items-center gap-1">
            View <ArrowUpRight size={12} />
          </a>
        }>
          {!production?.today_by_grade?.length ? (
            <div className="text-sm text-slate-400 py-4 text-center">No production logged yet today.</div>
          ) : (
            <div className="space-y-2">
              {production.today_by_grade.map((g) => (
                <div key={g.grade} className="flex items-center justify-between text-sm">
                  <span className="font-display font-semibold">{g.grade}</span>
                  <span className="tabular-nums text-slate-700">{g.qty_m3} m³</span>
                </div>
              ))}
            </div>
          )}
          {production?.low_stock_materials?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-xs font-display font-bold uppercase tracking-wider text-amber-700 mb-2">Low Stock</div>
              {production.low_stock_materials.map((m) => (
                <div key={m.material_id} className="flex items-center justify-between text-xs text-slate-600 py-0.5">
                  <span>{m.name}</span>
                  <span className="tabular-nums">{m.stock} (reorder @ {m.reorder_level})</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Lab Quality — this month" action={
          <a href="/app/lab" className="text-xs font-display font-bold uppercase tracking-widest text-[#1F7A6E] hover:underline flex items-center gap-1">
            View <ArrowUpRight size={12} />
          </a>
        }>
          {!lab ? (
            <div className="text-sm text-slate-400 py-4 text-center">No lab data available.</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 text-slate-600"><Clock size={13} /> Tests due</span>
                <span className="font-display font-bold tabular-nums">{lab.cube_tests_due}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 text-slate-600"><TrendingUp size={13} /> Tested this month</span>
                <span className="font-display font-bold tabular-nums">{lab.tested_this_month}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 text-emerald-700"><CheckCircle2 size={13} /> Passed</span>
                <span className="font-display font-bold tabular-nums text-emerald-700">{lab.passed_this_month}</span>
              </div>
              {lab.failed_this_month > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-red-700"><XCircle size={13} /> Failed</span>
                  <span className="font-display font-bold tabular-nums text-red-700">{lab.failed_this_month}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Material tests logged</span>
                <span className="font-display font-bold tabular-nums">{lab.material_test_count}</span>
              </div>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
